import axios from 'axios';
import type { Router } from 'vue-router';
import { useScraperStore } from '@/store/scraperStore';
import { useProjectsStore } from '@/store/projectsStore';
import { Project, Customer, UserStats } from '@/types/types';
import { getAuth } from 'firebase/auth';
import { handleAxiosError } from './errorHandler';
import api from './axiosBase';

import { API_BASE } from './apiBase';

/**
 * Creates a new project with a given name, then navigates.
 */
export const createProject = async (
  router: Router,
  projectName: string
): Promise<string | null> => {
  const store = useScraperStore();
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  try {
    // First: agent auth
    const authResponse = await axios.post(
      `${API_BASE}/v2/agent/auth`,
      {
        'DeepAnalysis-ID': store.deepAnalysisId
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    if (authResponse.status !== 200) {
      console.error('Agent authentication failed');
      return null;
    }

    // Then: create project
    const projectResponse = await axios.post(
      `${API_BASE}/v2/project`,
      {
        'Project-Name': projectName
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    if (projectResponse.status === 200) {
      console.log('Project creation successful');
      const SimpleID = projectResponse.data['Project-ID'];
      // Invalidate cached projects list so it refreshes on next visit
      try {
        useProjectsStore().lastFetched = 0;
      } catch {
        /* store may not be initialized */
      }
      router.push(`/organization/${SimpleID}`);
      return SimpleID;
    } else {
      console.error('Project creation failed');
      return null;
    }
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

/**
 * Retrieves an existing project (Tenant) by SimpleID.
 * @param SimpleID - The project ID
 * @param includeCustomers - Whether to include customers/users (default: true). Set to false for faster metadata-only fetch.
 */
export const getProject = async (
  SimpleID: string,
  includeCustomers = true
): Promise<Project | null> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  if (!SimpleID || SimpleID === 'undefined') {
    console.error('SimpleID is null or undefined');
    return null;
  }

  console.log(
    `[getProject] Fetching project with SimpleID: ${SimpleID} (includeCustomers: ${includeCustomers})`
  );
  console.log(`[getProject] Using idToken: ${idToken ? 'present' : 'missing'}`);

  try {
    const response = await api.get(`/v2/project/${SimpleID}`, {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { includeCustomers: includeCustomers.toString() }
    });

    console.log('Project retrieval response status:', response.status);
    console.log('Project retrieval response data:', response.data);

    if (response.status === 200) {
      // Log progress specifically
      if (response.data?.progress) {
        console.log('Project progress:', response.data.progress);
      } else {
        console.log('No progress data found in project response');
      }
      return response.data;
    } else {
      console.error('Project retrieval failed with status:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Project retrieval error:', error);
    handleAxiosError(error);
    return null;
  }
};

/**
 * Invite an agent to the project by passing inviteeEmail to the PUT endpoint.
 * The backend handles duplication checks, etc.
 */
export const inviteAgentToProject = async (
  projectSimpleID: string,
  inviterEmail: string,
  inviteeEmail: string,
  message: string
): Promise<boolean> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  const body = {
    invite: {
      inviteeEmail: inviteeEmail,
      inviterEmail: inviterEmail,
      message: message
    }
  };

  try {
    const response = await api.put(`/v2/project/${projectSimpleID}`, body, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    });
    if (response.status === 200) {
      console.log('Agent invited successfully:', response.data);
      return true;
    } else {
      console.error('Invite agent failed:', response.status, response.data);
      return false;
    }
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

/**
 * Updates the project's invite link access (public/private) and role, if public.
 *
 * @param projectSimpleID - The tenant/project ID (short ID).
 * @param linkUrl - (Optional) The specific invite link you want to update.
 *                  If omitted, the backend updates the first link in tenant.InviteLinks.
 * @param access - 'public' or 'private'.
 * @param role - Required if access is 'public'; not used if 'private'.
 * @returns true on success; false otherwise.
 */
export const updateInviteLink = async (
  projectSimpleID: string,
  linkUrl: string | undefined,
  access: 'public' | 'private',
  role?: string
): Promise<boolean> => {
  try {
    const auth = getAuth();
    const idToken =
      (await auth.currentUser?.getIdToken()) ||
      sessionStorage.getItem('idToken');

    // Per your backend, 'role' is required if access is 'public'
    if (access === 'public' && !role) {
      console.error('Role is required for public access');
      return false;
    }

    // Build the request body to match the Go "inviteLinksAccess" logic
    const body: Record<string, any> = {
      inviteLinksAccess: {
        access
      }
    };

    // If the user passes a specific linkUrl, include it; otherwise the backend
    // updates the first link in tenant.InviteLinks.
    if (linkUrl) {
      body.inviteLinksAccess.linkurl = linkUrl;
    }

    // For public links, also send along the role
    if (access === 'public' && role) {
      body.inviteLinksAccess.role = role;
    }

    const response = await api.put(`/v2/project/${projectSimpleID}`, body, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    });

    if (response.status === 200) {
      console.log('Invite link updated successfully:', response.data);
      return true;
    } else {
      console.error(
        'Updating invite link failed:',
        response.status,
        response.data
      );
      return false;
    }
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

/**
 * Fetch user stats for a given user in the project.
 */
export const getUserStats = async (
  ProjectID: string,
  SimpleID: string
): Promise<UserStats | null> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  try {
    const response = await axios.get(
      `${API_BASE}/v2/project/${ProjectID}/users/${SimpleID}/stats`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      console.error('User stats retrieval failed');
      return null;
    }
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return null;
  }
};

/**
 * Fetch customer details (for a user) if needed.
 */
export const getCustomerDetails = async (
  simpleId: string,
  tenantId: string
): Promise<Customer | null> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  try {
    const response = await axios.get(
      `${API_BASE}/v2/project/${tenantId}/users/${simpleId}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

/**
 * Fetch a single customer/user by SimpleID with messages hydrated
 * More efficient than fetching entire project when you only need one customer
 */
export const getCustomer = async (
  projectID: string,
  simpleID: string
): Promise<Customer | null> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  try {
    console.log(
      `[getCustomer] Fetching customer ${simpleID} from project ${projectID}`
    );
    const response = await api.get(
      `/v2/project/${projectID}/user/${simpleID}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    if (response.status === 200) {
      console.log('[getCustomer] Customer retrieved:', response.data);
      return response.data;
    } else {
      console.error('[getCustomer] Failed with status:', response.status);
      return null;
    }
  } catch (error) {
    console.error('[getCustomer] Error:', error);
    handleAxiosError(error);
    return null;
  }
};

/**
 * Updates a project (Tenant) with new fields (e.g. projectName, style, etc.)
 */
export const updateProject = async (
  updatedProject: Project
): Promise<boolean> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');

  try {
    console.log('Sending PUT request with data:', updatedProject);
    const response = await axios.put(
      `${API_BASE}/v2/project/${updatedProject.simpleID}`,
      updatedProject,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('API response:', response);

    if (response.status === 200) {
      console.log('Project updated successfully:', response.data);
      return true;
    } else {
      console.error('Project update failed:', response.status, response.data);
      return false;
    }
  } catch (error) {
    console.error('Error updating project:', error);
    handleAxiosError(error);
    return false;
  }
};

/**
 * Sends a request to join a project.
 */
export const requestJoin = async (simpleId: string): Promise<boolean> => {
  try {
    const response = await api.post(`/v2/project/${simpleId}/request-join`, {});
    if (response.status === 200) {
      console.log('Project join request sent successfully');
      return true;
    } else {
      console.error(
        'Project join request failed:',
        response.status,
        response.data
      );
      return false;
    }
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

/**
 * Updates the Stripe subscription plan.
 */
export const updateStripeSubscription = async (
  projectID: string,
  plan: 'none' | 'basic' | 'premium' | 'pro'
): Promise<boolean> => {
  console.log(
    'Updating Stripe subscription for project:',
    projectID,
    'to plan:',
    plan
  );

  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');
  try {
    console.log('Updating Stripe subscription to:', plan);
    const response = await api.post(
      `/v2/project/${projectID}/stripe`,
      { subscription: plan },
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );
    return response.status === 200;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

/**
 * Creates a Stripe Checkout Session and returns the session URL.
 */
export const createStripeCheckoutSession = async (
  projectID: string,
  plan: 'basic' | 'premium' | 'pro'
): Promise<string | null> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');
  try {
    const response = await api.post(
      `/v2/project/${projectID}/stripe/checkout`,
      { plan },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (response.status === 200 && typeof response.data.url === 'string') {
      return response.data.url;
    } else {
      console.error(
        'CreateCheckoutSession failed:',
        response.status,
        response.data
      );
      return null;
    }
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

/**
 * Fetch all active subscriptions the user can transfer from
 */
export const listTransferableSubscriptions = async (): Promise<
  Array<{
    projectId: string;
    projectName: string;
    plan: string;
    stripeSubscriptionId: string;
    status: string;
    currentPeriodEnd: string;
  }>
> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');
  try {
    const response = await api.get('/v2/subscriptions/transferable', {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return [];
  }
};

/**
 * Transfer a subscription from one project to another
 */
export const transferSubscription = async (
  sourceProjectId: string,
  targetProjectId: string
): Promise<boolean> => {
  const auth = getAuth();
  const idToken =
    (await auth.currentUser?.getIdToken()) || sessionStorage.getItem('idToken');
  try {
    const response = await api.post(
      '/v2/subscriptions/transfer',
      { sourceProjectId, targetProjectId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.status === 200;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

/**
 * Gets info about a pending access request
 * Note: Does NOT use global error handler since ApproveAccessView has its own error UI
 */
export const getPendingAccessRequest = async (
  projectId: string,
  email: string
): Promise<{
  already_member: boolean;
  project_name: string;
  project_id: string;
  requestee_email: string;
} | null> => {
  try {
    const response = await api.get(
      `/v2/project/${projectId}/pending-access?email=${encodeURIComponent(
        email
      )}`
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error getting pending access request:', error);
    // Don't call handleAxiosError here - ApproveAccessView has its own error handling
    return null;
  }
};

/**
 * Approves an access request for a user
 * Note: Does NOT use global error handler since ApproveAccessView has its own error UI
 */
export const approveAccess = async (
  projectId: string,
  requesteeEmail: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await api.post(`/v2/project/${projectId}/approve-access`, {
      requestee_email: requesteeEmail
    });
    if (response.status === 200) {
      return { success: true, message: response.data.message };
    }
    return { success: false, message: 'Failed to approve access' };
  } catch (error: unknown) {
    console.error('Error approving access:', error);
    // Don't call handleAxiosError here - ApproveAccessView has its own error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        message: axiosError.response?.data?.error || 'Failed to approve access'
      };
    }
    return { success: false, message: 'Failed to approve access' };
  }
};
